import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Cloud, Star, Sparkles } from 'lucide-react';

/**
 * Day/Night sky-scene toggle.
 * - A chunky pill that morphs between a bright sunny sky and a starry night.
 * - The sun/moon "ball" slides between the two ends with a bouncy spin.
 * - Clouds float across in light mode; stars twinkle in dark mode.
 */
export default function SkyToggle() {
  const { theme, toggle } = useTheme();
  const isDay = theme === 'light';

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDay ? 'Switch to night' : 'Switch to day'}
      title={isDay ? 'Daytime — click for night' : 'Nighttime — click for day'}
      className="group relative select-none focus:outline-none"
      style={{ width: 132, height: 56 }}
    >
      {/* The sky pill */}
      <div
        className="relative w-full h-full rounded-full border-[3px] overflow-hidden transition-all duration-500"
        style={{
          background: isDay
            ? 'linear-gradient(180deg, #7be1ff 0%, #ffe680 100%)'
            : 'linear-gradient(180deg, #0d052f 0%, #3a1064 100%)',
          borderColor: '#3a1064',
          boxShadow: isDay
            ? 'inset 0 -3px 0 rgba(0,0,0,0.08), 0 4px 0 #3a1064, 0 0 20px rgba(255,214,0,0.4)'
            : 'inset 0 -3px 0 rgba(255,255,255,0.05), 0 4px 0 #000, 0 0 18px rgba(157,61,255,0.45)',
        }}
      >
        {/* Day clouds */}
        <Cloud
          className="absolute transition-all duration-500"
          size={18}
          color="#ffffff"
          fill="#ffffff"
          style={{
            top: 8,
            left: isDay ? 72 : 140,
            opacity: isDay ? 0.95 : 0,
            filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.1))',
          }}
        />
        <Cloud
          className="absolute transition-all duration-700"
          size={12}
          color="#ffffff"
          fill="#ffffff"
          style={{
            bottom: 7,
            left: isDay ? 92 : 160,
            opacity: isDay ? 0.85 : 0,
          }}
        />

        {/* Night stars */}
        <Star
          className="absolute transition-all duration-500"
          size={9}
          color="#ffd900"
          fill="#ffd900"
          style={{
            top: 8,
            left: 72,
            opacity: isDay ? 0 : 1,
            transform: isDay ? 'scale(0)' : 'scale(1)',
          }}
        />
        <Star
          className="absolute transition-all duration-500 delay-75"
          size={6}
          color="#ffffff"
          fill="#ffffff"
          style={{
            top: 20,
            left: 90,
            opacity: isDay ? 0 : 1,
            transform: isDay ? 'scale(0)' : 'scale(1)',
          }}
        />
        <Sparkles
          className="absolute transition-all duration-500 delay-150"
          size={10}
          color="#ff2bd6"
          style={{
            bottom: 8,
            left: 104,
            opacity: isDay ? 0 : 1,
            transform: isDay ? 'scale(0) rotate(0)' : 'scale(1) rotate(15deg)',
          }}
        />
        <Star
          className="absolute transition-all duration-500"
          size={7}
          color="#00d4ff"
          fill="#00d4ff"
          style={{
            top: 12,
            left: 56,
            opacity: isDay ? 0 : 1,
            transform: isDay ? 'scale(0)' : 'scale(1)',
          }}
        />

        {/* Sun rays (light only) */}
        <div
          className="absolute transition-all duration-500"
          style={{
            top: 8,
            left: isDay ? 14 : 70,
            opacity: isDay ? 0.55 : 0,
            width: 32,
            height: 32,
            background:
              'radial-gradient(circle, rgba(255,214,0,0.9) 0%, rgba(255,214,0,0) 70%)',
            borderRadius: '50%',
          }}
        />

        {/* The sun / moon ball */}
        <div
          className="absolute top-1/2 -translate-y-1/2 rounded-full border-[3px] flex items-center justify-center transition-all duration-500 ease-out group-hover:scale-105 group-active:scale-95"
          style={{
            width: 38,
            height: 38,
            left: isDay ? 6 : 80,
            background: isDay
              ? 'radial-gradient(circle at 35% 30%, #ffffff 0%, #ffd900 45%, #ff9a1c 100%)'
              : 'radial-gradient(circle at 35% 30%, #ffffff 0%, #d6d6f0 25%, #8a82c4 80%)',
            borderColor: isDay ? '#a06c00' : '#1a0440',
            boxShadow: isDay
              ? '0 3px 0 #a06c00, 0 0 18px rgba(255,214,0,0.85)'
              : '0 3px 0 #1a0440, 0 0 16px rgba(214,214,240,0.5)',
            transform: `translateY(-50%) rotate(${isDay ? 0 : 180}deg)`,
          }}
        >
          {isDay ? (
            <Sun className="h-5 w-5 text-amber-900" strokeWidth={3} />
          ) : (
            <Moon className="h-5 w-5 text-purple-900" strokeWidth={3} fill="currentColor" />
          )}
          {/* moon craters */}
          {!isDay && (
            <>
              <span
                className="absolute rounded-full"
                style={{ width: 5, height: 5, top: 6, right: 7, background: '#9a92cf' }}
              />
              <span
                className="absolute rounded-full"
                style={{ width: 3, height: 3, bottom: 8, left: 8, background: '#9a92cf' }}
              />
            </>
          )}
        </div>

        {/* Label */}
        <span
          className="absolute font-black text-[10px] tracking-[2px] transition-all duration-500"
          style={{
            right: isDay ? 12 : 'auto',
            left: isDay ? 'auto' : 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: isDay ? '#3a1064' : '#ffd900',
            textShadow: isDay ? '0 1px 0 rgba(255,255,255,0.6)' : '0 1px 0 #000',
          }}
        >
          {isDay ? 'DAY' : 'NIGHT'}
        </span>
      </div>
    </button>
  );
}
